import { useEffect, useRef, useState } from "react";
import { LOCALE, tr } from "../i18n/idioma";
import { EnfoqueVideo } from "./videoComun";
import PieVideoPortada from "./PieVideoPortada";

/* Vídeo de YouTube que se ve en Yamacoa con controles propios (2026-10-25).
   - Antes de pulsar no se pide nada a YouTube: título y miniatura salen de /api/youtube
     (functions/api/youtube.js), que sirve la miniatura desde yamacoa.com.
   - Al pulsar se carga la API oficial del reproductor (IFrame API) sobre youtube-nocookie.com, sin
     los controles de YouTube (controls=0): reproducir/pausar, barra de progreso, tiempo, sonido y
     pantalla completa son de Yamacoa. «Ver en YouTube ↗» abre la fuente en otra pestaña.
   - «Cargar siempre…» se recuerda en este navegador; olvidarConsentimientoVideos() lo borra.
   - Si el vídeo ya no existe (oEmbed 401/403/404), se dice y no se muestra ninguna copia.
   Props: url; autor, titulo, fecha (respaldo); nota; enfoque; rotulo; portada (apertura de la portada: panorámico
   en el hueco de la imagen y con PieVideoPortada, de una línea, como pie). */

const CLAVE = "yamacoa.youtube.siempre";
export function olvidarConsentimientoYouTube() {
  try { localStorage.removeItem(CLAVE); } catch { /* sin almacenamiento */ }
}
const consentido = () => { try { return localStorage.getItem(CLAVE) === "1"; } catch { return false; } };
const recordar = () => { try { localStorage.setItem(CLAVE, "1"); } catch { /* se carga igual */ } };

export function idYouTube(url) {
  const m = /^https:\/\/(?:(?:www\.|m\.)?youtube\.com\/(?:watch\?v=|shorts\/|live\/|embed\/)|youtu\.be\/)([\w-]{11})/.exec(url || "");
  return m ? m[1] : null;
}
const vertical = (url) => /youtube\.com\/shorts\//.test(url || "");
const API = `${process.env.PUBLIC_URL || ""}/api/youtube`;

// La IFrame API de YouTube, una sola vez y solo tras el primer clic.
let cargaApi = null;
function apiYouTube() {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (!cargaApi) cargaApi = new Promise((ok, mal) => {
    const previo = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => { previo?.(); ok(window.YT); };
    const s = document.createElement("script");
    s.src = "https://www.youtube.com/iframe_api"; s.async = true;
    s.onerror = () => { cargaApi = null; mal(new Error("api")); };
    document.head.appendChild(s);
  });
  return cargaApi;
}

const reloj = (s) => { s = Math.max(0, Math.floor(s || 0)); const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), x = String(s % 60).padStart(2, "0"); return h ? `${h}:${String(m).padStart(2, "0")}:${x}` : `${m}:${x}`; };

const CSS = `
.yyt{margin:28px 0; padding:18px; border-radius:24px; background:var(--y-surface); box-shadow:var(--y-shadow);}
.yyt-pantalla{position:relative; width:100%; aspect-ratio:16/9; border-radius:16px; overflow:hidden; background:var(--y-pod-bg); color:var(--y-pod-ink);}
.yyt[data-vertical="1"] .yyt-pantalla{aspect-ratio:9/16; max-width:340px; margin:0 auto;}
.yyt-pantalla:fullscreen{border-radius:0; aspect-ratio:auto;}
.yyt-previa{position:absolute; inset:0; border:0; padding:0; cursor:pointer; background:none; color:inherit; width:100%;}
.yyt-previa img{width:100%; height:100%; object-fit:cover; display:block;}
.yyt-previa::after{content:""; position:absolute; inset:0; background:linear-gradient(180deg,rgba(31,26,22,0) 50%,rgba(31,26,22,.6));}
.yyt-previa:focus-visible{outline:3px solid var(--y-accent); outline-offset:-3px;}
.yyt-play{position:absolute; left:50%; top:50%; transform:translate(-50%,-50%); z-index:1; width:68px; height:68px; border-radius:50%;
  background:var(--y-surface); display:grid; place-items:center; box-shadow:0 6px 24px rgba(0,0,0,.25);}
.yyt-play i{display:block; width:0; height:0; border-left:19px solid var(--y-ink); border-top:12px solid transparent; border-bottom:12px solid transparent; margin-left:5px;}
.yyt-marca{position:absolute; left:14px; bottom:12px; z-index:1; font:600 10.5px var(--y-mono); letter-spacing:.08em; text-transform:uppercase;}
.yyt-marco{position:absolute; inset:0;} .yyt-marco iframe{width:100%; height:100%; border:0; display:block;}
.yyt-mandos{position:absolute; left:12px; right:12px; bottom:12px; z-index:2; display:flex; align-items:center; gap:12px; padding:8px 12px;
  border-radius:999px; background:rgba(31,26,22,.78); -webkit-backdrop-filter:blur(6px); backdrop-filter:blur(6px); color:var(--y-pod-ink);}
.yyt-pantalla:fullscreen .yyt-mandos{left:16px; right:16px; bottom:16px;}
.yyt-mandos button{flex:none; width:36px; height:36px; border-radius:50%; border:0; background:var(--y-surface); color:var(--y-ink); cursor:pointer;
  display:grid; place-items:center; font:700 13px var(--y-font);}
.yyt-mandos button.yyt-sec{background:rgba(252,249,244,.12); color:var(--y-pod-ink);}
.yyt-mandos button:focus-visible{outline:2px solid var(--y-pod-acc); outline-offset:2px;}
.yyt-barra{flex:1; min-width:60px; height:6px; -webkit-appearance:none; appearance:none; border-radius:3px; cursor:pointer;
  background:linear-gradient(90deg, var(--y-prog-a), var(--y-prog-b)) 0/var(--p,0%) 100% no-repeat, rgba(252,249,244,.22);}
.yyt-barra::-webkit-slider-thumb{-webkit-appearance:none; width:14px; height:14px; border-radius:50%; background:var(--y-pod-ink);}
.yyt-barra::-moz-range-thumb{width:14px; height:14px; border:0; border-radius:50%; background:var(--y-pod-ink);}
.yyt-t{font:500 11px var(--y-mono); color:var(--y-pod-sec); white-space:nowrap;}
.yyt .yyt-txt{margin-top:14px;}
.yyt .yyt-txt p{color:inherit; margin:0;}
.yyt .yyt-txt p.y-eyebrow{font:500 11px var(--y-mono); letter-spacing:.08em; text-transform:uppercase; color:var(--y-accent); margin:0 0 8px;}
.yyt .yyt-txt p.y-meta{font:400 10.5px var(--y-mono); letter-spacing:.06em; color:var(--y-subtle);}
.yyt .yyt-txt .yyt-tit{font-size:16px; line-height:1.45; color:var(--y-ink); font-weight:600; margin:0 0 6px;}
.yyt .ytt-nota{margin:12px 0; padding:10px 14px; border-left:3px solid var(--y-auto-ink); background:var(--y-auto-bg); border-radius:0 12px 12px 0;}
.yyt .ytt-nota b{display:block; font:600 10.5px var(--y-mono); letter-spacing:.06em; text-transform:uppercase; color:var(--y-auto-ink); margin-bottom:4px;}
.yyt .ytt-nota p{margin:0; font-size:14.5px; line-height:1.5; color:var(--y-auto-head);}
.yyt .yyt-txt .yyt-aviso{font-size:13.5px; line-height:1.5; color:var(--y-muted); margin:10px 0;}
.yyt-casilla{display:flex; gap:8px; align-items:flex-start; font-size:13.5px; color:var(--y-ink-2); cursor:pointer; margin:8px 0 0;}
.yyt-casilla input{margin-top:3px; accent-color:var(--y-ink);}
.yyt-pie{display:flex; flex-wrap:wrap; gap:8px 14px; align-items:center; margin-top:14px; padding-top:12px; border-top:1px solid var(--y-rule);}
.yyt-pie a{font-weight:700; font-size:14px; color:var(--y-ink);}
.yyt--portada{margin:24px 0 0; padding:0; border-radius:24px; overflow:hidden; background:var(--y-surface-2); box-shadow:none;}
.yyt--portada .yyt-pantalla{border-radius:0;}
.yyt-pie-portada{font:500 10px var(--y-mono); letter-spacing:.06em; color:var(--y-subtle); padding:8px 12px;}
@media (max-width:640px){ .yyt{padding:14px;} .yyt-mandos{gap:8px; padding:6px 10px;} .yyt-mandos button{width:32px; height:32px;} }
`;

export default function VideoYouTube({ url, autor, titulo, fecha, nota, enfoque, rotulo, portada = false }) {
  const id = idYouTube(url);
  const [meta, setMeta] = useState(null);
  const [retirado, setRetirado] = useState(false);
  const [on, setOn] = useState(false);
  const [siempre, setSiempre] = useState(false);
  const [fallo, setFallo] = useState(false);
  const [st, setSt] = useState({ suena: false, t: 0, total: 0, mudo: false });
  const pantalla = useRef(null), marco = useRef(null), jugador = useRef(null);
  const porClic = useRef(false); // con «cargar siempre» el vídeo se prepara pero no arranca solo

  useEffect(() => {
    let vivo = true;
    fetch(`${API}?url=${encodeURIComponent(url)}`)
      .then(async (r) => {
        if (!(r.headers.get("content-type") || "").includes("json")) return;
        const j = await r.json();
        if (!vivo) return;
        if (j.retirado) setRetirado(true); else if (r.ok) setMeta(j);
      }).catch(() => {});
    if (consentido()) setOn(true);
    return () => { vivo = false; };
  }, [url]);

  // Al pulsar (o con «cargar siempre»): el reproductor sin controles de YouTube.
  useEffect(() => {
    if (!on || !id) return;
    let vivo = true, tic = null;
    apiYouTube().then((YT) => {
      if (!vivo || !marco.current) return;
      const div = document.createElement("div"); marco.current.appendChild(div);
      jugador.current = new YT.Player(div, {
        host: "https://www.youtube-nocookie.com", videoId: id, width: "100%", height: "100%",
        playerVars: { autoplay: porClic.current ? 1 : 0, controls: 0, rel: 0, playsinline: 1, modestbranding: 1,
          iv_load_policy: 3, cc_load_policy: 0, disablekb: 0, origin: window.location.origin },
        events: {
          onReady: (e) => setSt((s) => ({ ...s, total: e.target.getDuration() || 0, mudo: e.target.isMuted() })),
          onStateChange: (e) => setSt((s) => ({ ...s, suena: e.data === 1, total: e.target.getDuration() || s.total })),
          onError: () => setFallo(true),
        },
      });
      tic = setInterval(() => {
        const p = jugador.current;
        if (p?.getCurrentTime) setSt((s) => ({ ...s, t: p.getCurrentTime() || 0 }));
      }, 250);
    }).catch(() => setFallo(true));
    return () => { vivo = false; clearInterval(tic); try { jugador.current?.destroy(); } catch { /* ya no está */ } jugador.current = null; };
  }, [on, id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (retirado) {
    return (
      <div className="y-vacio" style={{ margin: "24px 0" }}>
        {tr("Este vídeo de YouTube ya no está disponible. Puede que su autor lo haya retirado.",
          "This YouTube video is no longer available. Its author may have removed it.")}
      </div>
    );
  }
  if (!id) return null;

  const p = jugador.current;
  const t = meta?.titulo || titulo, quien = meta?.autor || autor, cuando = meta?.fecha || fecha;
  const enlace = meta?.url || url;
  const cargar = () => { if (siempre) recordar(); porClic.current = true; setOn(true); };
  const alternar = () => { if (!p?.getPlayerState) return; st.suena ? p.pauseVideo() : p.playVideo(); };
  const mudo = () => { if (!p?.isMuted) return; if (p.isMuted()) p.unMute(); else p.mute(); setSt((s) => ({ ...s, mudo: !s.mudo })); };
  const completa = () => {
    const el = pantalla.current;
    if (document.fullscreenElement) document.exitFullscreen?.(); else el?.requestFullscreen?.();
  };
  const pct = st.total ? (st.t / st.total) * 100 : 0;

  return (
    <figure className={portada ? "yyt yyt--portada" : "yyt"} data-vertical={!portada && vertical(url) ? "1" : "0"}>
      <style>{CSS}</style>
      <div className="yyt-pantalla" ref={pantalla}>
        {on ? <div className="yyt-marco" ref={marco} /> : (
          <button type="button" className="yyt-previa" onClick={cargar}
            aria-label={tr("Reproducir el vídeo (se carga desde YouTube)", "Play the video (loads from YouTube)")}>
            <img src={meta?.miniatura || `${API}?miniatura=1&url=${encodeURIComponent(url)}`} alt="" loading="lazy"
              onError={(e) => { e.currentTarget.style.display = "none"; }} />
            <span className="yyt-play" aria-hidden="true"><i /></span>
            <span className="yyt-marca" aria-hidden="true">YouTube</span>
          </button>
        )}
        {on && !fallo && (
          <div className="yyt-mandos" role="group" aria-label={tr("Controles del vídeo", "Video controls")}>
            <button type="button" onClick={alternar} aria-label={st.suena ? tr("Pausar", "Pause") : tr("Reproducir", "Play")}>
              {st.suena ? "❚❚" : "▶"}
            </button>
            <input type="range" className="yyt-barra" min="0" max={st.total || 0} step="0.5" value={Math.min(st.t, st.total || 0)}
              style={{ "--p": `${pct}%` }} aria-label={tr("Posición del vídeo", "Video position")}
              aria-valuetext={`${reloj(st.t)} / ${reloj(st.total)}`}
              onChange={(e) => { const v = Number(e.target.value); setSt((s) => ({ ...s, t: v })); p?.seekTo?.(v, true); }} />
            <span className="yyt-t">{reloj(st.t)} / {reloj(st.total)}</span>
            <button type="button" className="yyt-sec" onClick={mudo} aria-label={st.mudo ? tr("Activar sonido", "Unmute") : tr("Silenciar", "Mute")}>
              {st.mudo ? "🔇" : "🔊"}
            </button>
            <button type="button" className="yyt-sec" onClick={completa} aria-label={tr("Pantalla completa", "Full screen")}>⛶</button>
          </div>
        )}
      </div>
      {fallo && <p className="y-meta" role="alert" style={{ marginTop: 10 }}>{tr("No se ha podido cargar aquí.", "It could not be loaded here.")}{" "}
        <a href={enlace} target="_blank" rel="noopener noreferrer nofollow">{tr("Ábrelo en YouTube ↗", "Open it on YouTube ↗")}</a></p>}
      {portada ? <PieVideoPortada enfoque={enfoque} autor={quien} cargado={on} /> : <figcaption className="yyt-txt">
        <p className="y-eyebrow">{rotulo || tr("Vídeo en YouTube", "Video on YouTube")}</p>
        <EnfoqueVideo enfoque={enfoque} />
        {t && <p className="yyt-tit">{t}</p>}
        <p className="y-meta">
          {quien}
          {cuando && ` · ${tr("publicado el", "posted on")} ${new Date(cuando).toLocaleDateString(LOCALE(), { day: "numeric", month: "long", year: "numeric" })}`}
        </p>
        {nota && <div className="ytt-nota"><b>{tr("Nota de Yamacoa", "Yamacoa note")}</b><p>{nota}</p></div>}
        {!on && (
          <>
            <p className="yyt-aviso">{tr("Este vídeo se reproduce aquí, pero lo sirve YouTube. Al pulsar, YouTube (Google) recibirá tu IP y podrá usar sus cookies.",
              "This video plays here, but YouTube serves it. When you press play, YouTube (Google) will receive your IP address and may use its cookies.")}</p>
            <label className="yyt-casilla">
              <input type="checkbox" checked={siempre} onChange={(e) => setSiempre(e.target.checked)} />
              {tr("Cargar siempre los vídeos de YouTube en este navegador", "Always load YouTube videos in this browser")}
            </label>
          </>
        )}
        <div className="yyt-pie">
          {quien && <span className="y-meta">{tr(`${quien} en YouTube`, `${quien} on YouTube`)}</span>}
          <a href={enlace} target="_blank" rel="noopener noreferrer nofollow">{tr("Ver en YouTube ↗", "View on YouTube ↗")}</a>
        </div>
      </figcaption>}
    </figure>
  );
}
